/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ColorSwatchPicker,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useVouchers } from "../../store/useVouchers";
import { useEffect, useState } from "react";
import { VoucherUsersTable } from "./VoucherUsersTable";
import dayjs from "dayjs";
import { ReactComponent as LeftArrow } from "../../assets/icons/arrows/left-arrow.svg";
import { usePartnerChallenges } from "../../store/usePartnerChallenges";
import { useAlert } from "../../store/useAlert";
import { useLottery } from "../../store/useLottery";
import { Switch } from "@material-tailwind/react";

function VoucherDetailsScreen() {
  /** Params */
  const params = useParams();
  /** Hooks */
  const navigate = useNavigate();
  const {
    oneVoucher,
    voucherUsers,
    voucherUsersPagination,
    getVoucherUsers,
    updateVoucher,
    clearVoucherUsers,
  } = useVouchers();
  const { allPartnerChallenges, getAllPartnerChallenges } =
    usePartnerChallenges();
  const { allLotteries, getAllLotteries } = useLottery();
  const { setAlert } = useAlert();
  /** State */
  const [usersPage, setUsersPage] = useState<number>(1);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState({
    value: "",
    prizeName: "",
    isProduct: false,
    belongsTo: "NONE" as "NONE" | "CHALLENGE" | "LOTTERY",
    challenge: "",
    lottery: "",
    expireDate: "",
    description: "",
    instructions: "",
    link: "",
    benefitText: "",
    color: "#FF5726",
    allowMultipleRedemptions: false,
  });

  /** Effects */
  useEffect(() => {
    if (params?.id) {
      // Single endpoint call that gets both voucher details and users
      getVoucherUsers(params.id, 1, 15);
      getAllPartnerChallenges();
      getAllLotteries(1, 1000);
    }
    return () => {
      clearVoucherUsers();
    };
  }, [params?.id]);

  useEffect(() => {
    const challengeId = resolveId(oneVoucher?.challenge) || "";
    const lotteryId = resolveId(oneVoucher?.lottery) || "";
    const belongsTo =
      oneVoucher?.sourceType === "CHALLENGE" || challengeId
        ? "CHALLENGE"
        : oneVoucher?.sourceType === "LOTTERY" || lotteryId
          ? "LOTTERY"
          : "NONE";
    setFormValues({
      value: oneVoucher?.value ? String(oneVoucher.value) : "",
      prizeName: (oneVoucher as any)?.prizeName || "",
      isProduct: Boolean((oneVoucher as any)?.isProduct),
      belongsTo,
      challenge: challengeId,
      lottery: lotteryId,
      expireDate: oneVoucher?.expireDate
        ? dayjs(oneVoucher.expireDate).format("YYYY-MM-DD")
        : "",
      description: oneVoucher?.description || "",
      instructions: oneVoucher?.instructions || "",
      link: oneVoucher?.link || "",
      benefitText: oneVoucher?.benefitText || "",
      color: oneVoucher?.color || "#FF5726",
      allowMultipleRedemptions:
        oneVoucher?.allowMultipleRedemptions === true ||
        oneVoucher?.allowMultipleRedemptions === "true" ||
        oneVoucher?.allowMultipleRedeems === true,
    });
  }, [oneVoucher]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const resolveUsageLabel = () => {
    if (oneVoucher?.sourceType) {
      if (oneVoucher.sourceType === "LOTTERY") return "Lottery";
      if (oneVoucher.sourceType === "CHALLENGE") return "Challenge";
      return "Unknown";
    }
    if (oneVoucher?.lottery) return "Lottery";
    if (oneVoucher?.challenge) return "Challenge";
    return "Unknown";
  };

  const resolveId = (val: any): string | null => {
    if (!val) return null;
    if (typeof val === "string") return val;
    if (typeof val === "object" && val?._id) return String(val._id);
    return null;
  };

  const handleLoadMore = () => {
    const nextPage = usersPage + 1;
    setUsersPage(nextPage);
    // Use the same limit as initial load (1 in this case, but should match backend)
    const limit = voucherUsersPagination?.itemsPerPage || 1;
    getVoucherUsers(params?.id as string, nextPage, limit, true);
  };

  const handleCancelEdit = () => {
    const challengeId = resolveId(oneVoucher?.challenge) || "";
    const lotteryId = resolveId(oneVoucher?.lottery) || "";
    const belongsTo =
      oneVoucher?.sourceType === "CHALLENGE" || challengeId
        ? "CHALLENGE"
        : oneVoucher?.sourceType === "LOTTERY" || lotteryId
          ? "LOTTERY"
          : "NONE";
    setFormValues({
      value: oneVoucher?.value ? String(oneVoucher.value) : "",
      prizeName: (oneVoucher as any)?.prizeName || "",
      isProduct: Boolean((oneVoucher as any)?.isProduct),
      belongsTo,
      challenge: challengeId,
      lottery: lotteryId,
      expireDate: oneVoucher?.expireDate
        ? dayjs(oneVoucher.expireDate).format("YYYY-MM-DD")
        : "",
      description: oneVoucher?.description || "",
      instructions: oneVoucher?.instructions || "",
      link: oneVoucher?.link || "",
      benefitText: oneVoucher?.benefitText || "",
      color: oneVoucher?.color || "#FF5726",
      allowMultipleRedemptions:
        oneVoucher?.allowMultipleRedemptions === true ||
        oneVoucher?.allowMultipleRedemptions === "true" ||
        oneVoucher?.allowMultipleRedeems === true,
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!params?.id) return;
    const partnerId = resolveId(oneVoucher?.partnerId);
    const isProduct = formValues.isProduct;
    if (!isProduct && !oneVoucher?.code) {
      setAlert({
        isOpen: true,
        message: "Voucher is missing required fields (code).",
        severity: "error",
      });
      return;
    }
    try {
      await updateVoucher(params.id, {
        ...(oneVoucher?.code ? {code: oneVoucher.code} : {}),
        ...(partnerId ? {partnerId} : {}),
        type: oneVoucher?.type || "FIXED_AMOUNT",
        status: oneVoucher?.status || "ACTIVE",
        value: formValues.isProduct ? undefined : (formValues.value || undefined),
        isProduct: formValues.isProduct,
        prizeName: formValues.isProduct ? (formValues.prizeName || undefined) : undefined,
        sourceType:
          formValues.belongsTo === "NONE" ? "UNKNOWN" : formValues.belongsTo,
        challenge:
          formValues.belongsTo === "CHALLENGE"
            ? formValues.challenge || null
            : null,
        lottery:
          formValues.belongsTo === "LOTTERY"
            ? formValues.lottery || null
            : null,
        // Sent even when empty: undefined drops the key from the request, and
        // the backend then keeps the old text, so a cleared field came back.
        description: formValues.description ?? "",
        instructions: formValues.instructions ?? "",
        link: formValues.link ?? "",
        benefitText: formValues.benefitText ?? "",
        color: formValues.color,
        expireDate: formValues.expireDate || undefined,
        allowMultipleRedemptions: formValues.allowMultipleRedemptions,
      });
      await getVoucherUsers(
        params.id,
        1,
        voucherUsersPagination?.itemsPerPage || 15
      );
      setUsersPage(1);
      setIsEditing(false);
      setAlert({
        isOpen: true,
        message: "Voucher updated successfully.",
        severity: "success",
      });
    } catch {
      setAlert({
        isOpen: true,
        message: "Failed to update voucher.",
        severity: "error",
      });
    }
  };

  return (
    <div>
      <div className="flex flex-row items-center justify-between mb-6">
        <div className="flex flex-row items-center">
          <LeftArrow onClick={goBack} className="mr-3 cursor-pointer" />
          <h1 className="font-satoshi text-2xl font-bold text-dark">
            Voucher Details
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <SecondaryButton
                type="button"
                onClick={handleCancelEdit}
                label="Cancel"
              />
              <PrimaryButton type="button" onClick={handleSave} label="Save" />
            </>
          ) : (
            <PrimaryButton
              type="button"
              onClick={() => setIsEditing(true)}
              label="Edit"
            />
          )}
          <SecondaryButton type="button" onClick={goBack} label="Back" />
        </div>
      </div>

      {/* Voucher Details */}
      <div className="w-full bg-white  border-gray-border drop-shadow-none rounded-xl mb-6">
        <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
          <p className="font-satoshi font-medium text-base text-dark">
            Voucher Information
          </p>
        </div>
        <div className="w-full px-7 py-7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {!(oneVoucher as any)?.isProduct && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code
                </label>
                <p className="font-satoshi text-base text-dark">
                  {oneVoucher?.code || "-"}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              {isEditing ? (
                <>
                  <Switch
                    crossOrigin={"anonymous"}
                    color="teal"
                    ripple
                    label="Product voucher (physical prize)"
                    checked={formValues.isProduct}
                    disabled
                    onChange={() => {}}
                  />
                  {formValues.isProduct && (
                    <p className="mt-1 text-xs text-amber-600">
                      Only supported in the redesign app. Current app users will not see this voucher correctly.
                    </p>
                  )}
                </>
              ) : (
                <p className="font-satoshi text-base text-dark">
                  {(oneVoucher as any)?.isProduct ? (
                    <span className="inline-flex items-center gap-1.5">
                      Product
                      <span className="text-xs text-amber-600 font-normal">(redesign app only)</span>
                    </span>
                  ) : "Code"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {formValues.isProduct ? "Prize name" : "Value"}
              </label>
              {isEditing ? (
                formValues.isProduct ? (
                  <input
                    type="text"
                    className="w-full border border-gray-border rounded-lg px-3 py-2"
                    placeholder="e.g., Thermos, Coffee mug"
                    value={formValues.prizeName}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        prizeName: e.target.value,
                      }))
                    }
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      className="w-full border border-gray-border rounded-lg px-3 py-2"
                      placeholder="e.g., 2500 for €25"
                      value={formValues.value}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          value: e.target.value,
                        }))
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      A plain number is read as cents: 500 shows as &euro;5.00
                      and 5 as &euro;0.05. Write &euro;5, 20% or any text to
                      have it shown exactly as typed. Decimals are not
                      supported.
                    </p>
                  </>
                )
              ) : (
                <p className="font-satoshi text-base text-dark">
                  {(oneVoucher as any)?.isProduct
                    ? (oneVoucher as any)?.prizeName || "No prize name"
                    : oneVoucher?.value || "No value"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usage
              </label>
              {isEditing ? (
                <select
                  className="w-full border border-gray-border rounded-lg px-3 py-2"
                  value={formValues.belongsTo}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      belongsTo: e.target.value as
                        | "NONE"
                        | "CHALLENGE"
                        | "LOTTERY",
                    }))
                  }
                >
                  <option value="NONE">Standalone / None</option>
                  <option value="CHALLENGE">Challenge</option>
                  <option value="LOTTERY">Lottery</option>
                </select>
              ) : (
                <p className="font-satoshi text-base text-dark">
                  {resolveUsageLabel()}
                </p>
              )}
            </div>
            {(!isEditing || formValues.belongsTo === "LOTTERY") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Belongs to lottery
                </label>
                {isEditing ? (
                  <select
                    className="w-full border border-gray-border rounded-lg px-3 py-2"
                    value={formValues.lottery}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        lottery: e.target.value,
                      }))
                    }
                  >
                    <option value="">No lottery</option>
                    {allLotteries.map((lottery) => (
                      <option key={lottery._id} value={lottery._id}>
                        {lottery.en_title || lottery.nl_title || lottery._id}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p
                    onClick={() => {
                      const id = resolveId(oneVoucher?.lottery);
                      if (id) navigate(`/edit-lottery/${id}`);
                    }}
                    className={`font-satoshi text-base ${
                      resolveId(oneVoucher?.lottery)
                        ? "text-blue-700 underline cursor-pointer"
                        : "text-dark"
                    }`}
                  >
                    {oneVoucher?.lottery
                      ? typeof oneVoucher.lottery === "object"
                        ? // @ts-ignore
                          oneVoucher.lottery?.en_title ||
                          // @ts-ignore
                          oneVoucher.lottery?.nl_title ||
                          // @ts-ignore
                          oneVoucher.lottery?._id ||
                          "-"
                        : String(oneVoucher.lottery)
                      : "-"}
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Belongs to challenge
              </label>
              {isEditing && formValues.belongsTo === "CHALLENGE" ? (
                <select
                  className="w-full border border-gray-border rounded-lg px-3 py-2"
                  value={formValues.challenge}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      challenge: e.target.value,
                    }))
                  }
                >
                  <option value="">No challenge</option>
                  {allPartnerChallenges.map((challenge) => (
                    <option key={challenge._id} value={challenge._id}>
                      {challenge.en_title ||
                        challenge.nl_title ||
                        challenge._id}
                    </option>
                  ))}
                </select>
              ) : isEditing ? (
                <p className="font-satoshi text-base text-dark">-</p>
              ) : (
                <p
                  onClick={() => {
                    const id = resolveId(oneVoucher?.challenge);
                    if (id) navigate(`/edit-partner-challenge/${id}`);
                  }}
                  className={`font-satoshi text-base ${
                    resolveId(oneVoucher?.challenge)
                      ? "text-blue-700 underline cursor-pointer"
                      : "text-dark"
                  }`}
                >
                  {oneVoucher?.challenge
                    ? typeof oneVoucher.challenge === "object"
                      ? // @ts-ignore
                        oneVoucher.challenge?.title ||
                        // @ts-ignore
                        oneVoucher.challenge?.en_title ||
                        // @ts-ignore
                        oneVoucher.challenge?.nl_title ||
                        // @ts-ignore
                        oneVoucher.challenge?.name ||
                        // @ts-ignore
                        oneVoucher.challenge?._id ||
                        "-"
                      : String(oneVoucher.challenge)
                    : "-"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <p className="font-satoshi text-base text-dark">
                {oneVoucher?.status || "-"}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expire Date
              </label>
              {isEditing ? (
                <input
                  type="date"
                  className="w-full border border-gray-border rounded-lg px-3 py-2"
                  value={formValues.expireDate}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      expireDate: e.target.value,
                    }))
                  }
                />
              ) : (
                <p className="font-satoshi text-base text-dark">
                  {oneVoucher?.expireDate
                    ? dayjs(oneVoucher.expireDate).format("DD/MM/YYYY")
                    : "No expire date"}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Redeemed by multiple users
              </label>
              {isEditing ? (
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-droppie-oranje cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    checked={formValues.isProduct ? false : formValues.allowMultipleRedemptions}
                    disabled={formValues.isProduct}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        allowMultipleRedemptions: e.target.checked,
                      }))
                    }
                  />
                  <span className="font-satoshi text-base text-dark">
                    {!formValues.isProduct && formValues.allowMultipleRedemptions ? "Yes" : "No"}
                  </span>
                </label>
              ) : (
                <p className="font-satoshi text-base text-dark">
                  {oneVoucher?.allowMultipleRedeems ||
                  oneVoucher?.allowMultipleRedemptions === true ||
                  oneVoucher?.allowMultipleRedemptions === "true"
                    ? "Yes"
                    : "No"}
                </p>
              )}
            </div>
          </div>
          {(isEditing || oneVoucher?.description) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              {isEditing ? (
                <textarea
                  className="w-full border border-gray-border rounded-lg px-3 py-2 min-h-[88px]"
                  value={formValues.description}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              ) : (
                <p className="font-satoshi text-base text-dark">
                  {oneVoucher?.description}
                </p>
              )}
            </div>
          )}
          {(isEditing || oneVoucher?.instructions) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructions
              </label>
              {isEditing ? (
                <textarea
                  className="w-full border border-gray-border rounded-lg px-3 py-2 min-h-[88px]"
                  value={formValues.instructions}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      instructions: e.target.value,
                    }))
                  }
                />
              ) : (
                <p className="font-satoshi text-base text-dark">
                  {oneVoucher?.instructions}
                </p>
              )}
            </div>
          )}
          {(isEditing || oneVoucher?.link) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link <span className="text-gray-400">(optional)</span>
              </label>
              {isEditing ? (
                <>
                  <input
                    type="url"
                    className="w-full border border-gray-border rounded-lg px-3 py-2"
                    placeholder="e.g., https://www.partner.nl/actie"
                    value={formValues.link}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        link: e.target.value,
                      }))
                    }
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Shown as one tappable line at the bottom of the voucher.
                  </p>
                </>
              ) : (
                <p className="font-satoshi text-base text-dark break-all">
                  {oneVoucher?.link}
                </p>
              )}
            </div>
          )}
          {(isEditing || oneVoucher?.benefitText) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Benefit Text
              </label>
              {isEditing ? (
                <textarea
                  className="w-full border border-gray-border rounded-lg px-3 py-2 min-h-[88px]"
                  value={formValues.benefitText}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      benefitText: e.target.value,
                    }))
                  }
                />
              ) : (
                <p className="font-satoshi text-base text-dark">
                  {oneVoucher?.benefitText}
                </p>
              )}
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            {isEditing ? (
              <ColorSwatchPicker
                label=""
                value={formValues.color}
                onChange={(color) =>
                  setFormValues((prev) => ({
                    ...prev,
                    color,
                  }))
                }
              />
            ) : (
              <div
                style={{
                  backgroundColor: oneVoucher?.color || "#FF5726",
                }}
                className="w-8 h-8 rounded-full ring-1 ring-gray-border"
              />
            )}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="mt-6">
        <div className="flex w-full justify-between mb-3">
          <h2 className="font-satoshi text-xl font-bold text-dark">
            Redeemed Users
            {voucherUsersPagination
              ? ` (${voucherUsersPagination.totalItems})`
              : ""}
          </h2>
        </div>
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          {!voucherUsers?.length ? (
            <p className="text-center font-satoshi text-base text-dark">
              This voucher has not been redeemed by any user yet.
            </p>
          ) : voucherUsersPagination ? (
            <VoucherUsersTable
              users={voucherUsers}
              pagination={voucherUsersPagination}
              onLoadMore={handleLoadMore}
            />
          ) : (
            <VoucherUsersTable
              users={voucherUsers}
              pagination={{
                currentPage: 1,
                totalPages: 1,
                totalItems: voucherUsers.length,
                hasNextPage: false,
                hasPreviousPage: false,
              }}
              onLoadMore={handleLoadMore}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(VoucherDetailsScreen);
